/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, Input } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil, scan, map } from 'rxjs/operators';

import { PlanState } from 'src/app/services/plan-state.service';

import { Swimlane, SwimlaneRenderTypes } from 'src/app/app-data/models/Swimlane';
import { SwimlaneWithSelfObserver } from 'src/app/common/fancy-firebase-ticket/lists/SwimlaneList';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService, ILoggerService } from '@touchplan/logging';

@Component({
	selector: 'swimlanes',
	templateUrl: './swimlanes.component.html',
	host: { class: 'swimlanes' },
	// styles: [':host { position: absolute; top: 0; left: 0; z-index: 1000000;}'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LoggerServiceFactory.provide('SwimlanesComponent')],
})
export class SwimlanesComponent implements OnInit, OnDestroy {
	// @Input() swimlanes: Map<string, Swimlane>
	private shutdownSubject = new Subject<void>();
	public swimlanesSync: SwimlaneWithSelfObserver[];
	public swimlane$: Observable<SwimlaneWithSelfObserver[]>;
	public includeTickets = false;

	constructor(private planState: PlanState, private logger: DefaultLoggerService) {}

	private sanitizeSwimlaneName(name: string | undefined) {
		return name ? name.trim() : '';
	}

	public close(swimlane: Swimlane) {
		this.logger.log('close', swimlane);

		const fullList = this.planState.swimlanes.data.syncList;

		let duplicateNameFound = false;
		const trimmedLabelA = this.sanitizeSwimlaneName(swimlane.view?.label);

		fullList.forEach((listSwimlane) => {
			//check database not view to avoid edge case where user can bypass the duplication check
			//by editing multiple swimlanes at once and refreshing without saving all of them
			const trimmedLabelB = this.sanitizeSwimlaneName(listSwimlane.database?.label);

			if (trimmedLabelB === trimmedLabelA && listSwimlane.$id !== swimlane.$id) {
				duplicateNameFound = true;
			}
		});

		if (duplicateNameFound) {
			Logging.warning('Swimlane names must be unique within a plan.');
			return;
		}

		this.planState.swimlanes.changeView(swimlane, SwimlaneRenderTypes.database);
		this.planState.swimlanes.persistData(swimlane);
	}
	public delete(swimlane: Swimlane) {
		this.logger.log('delete', swimlane);
		// this.planState.swimlanes.abortView(swimlane, SwimlaneRenderTypes.database);
		this.planState.swimlanes.removeItem(swimlane);
	}
	public update(data: { model: Swimlane; data: Object }) {
		this.logger.log('update', data);
		this.planState.swimlanes.localUpdate(data.model.$id, data.data);
	}

	public includeTicketsUpdated(state: boolean) {
		this.includeTickets = state;
		this.logger.log('state', state, 'includeTickets', this.includeTickets);
		// setInterval(()=> this.logger.log('state', state, 'includeTickets', this.includeTickets), 1000);
		this.planState.swimlaneDragService.includeTickets = state;
	}

	public ngOnInit() {
		this.logger.log('components inited', performance.now() - window.blehTimestamp);
		this.includeTickets = this.planState.swimlaneDragService.includeTickets;

		this.swimlane$ = this.planState.swimlanes.data.editList$.pipe(
			takeUntil(this.shutdownSubject),
			map((mapBasedList) => Array.from(mapBasedList.values())) //todo create an operator that converts a map to an array
		);
		this.swimlane$.subscribe((swimlanes) => {
			this.swimlanesSync = swimlanes;
			// this.logger.log('this.swimlanes', this.swimlanesSync);
		});
	}

	public ngOnDestroy() {
		if (this.planState.swimlaneDragService) {
			this.planState.swimlaneDragService.includeTickets = true;
		}

		this.logger.log('destroy swimlanes component');
		this.shutdownSubject.next();
	}
}

// Musings
// - can't ngFor a map efficiently, some processing will be necessary
// - current setup for the selected maps the collection generates is to only emit updates when the selection itself changes, not when the tickets involved in the selection change, swimlanes (and probably other things) will require monitoring ticket data
// - when unwrapping the selected tickets observable, then iterating it's children, the children will lose the magic OnPush properties that observables get when being passed to the child component (ngFor?) so they wil not be change detected
// - look into combining the selection and additional events to that selection, then emitting an array of observables, one for each swimlane
//
// potential solution:
//
// selection.pipe(
// 	switchMap(selectionList:Map => {
// 		rawEvent$.pipe(
// 			filter(action => selectionList.has(action.key)),
//
// 		)
// 	})
// )
